import type { SyntheticEvent } from 'react';
import React, { useState } from 'react';

import type { RestoreIndex } from 'data-lake/Types';
import { IfPermitted, Timestamp } from 'components/common';
import { LinkContainer } from 'components/common/router';
import SearchLink from 'views/logic/search/SearchLink';
import { createElasticsearchQueryString } from 'views/logic/queries/Query';
import { Button, BootstrapModalConfirm, ButtonToolbar } from 'components/bootstrap';
import useSendTelemetry from 'logic/telemetry/useSendTelemetry';
import useLocation from 'routing/useLocation';
import { getPathnameWithoutId } from 'util/URLUtils';
import { TELEMETRY_EVENT_TYPE } from 'telemetry/Constants';

import type { DataLakeRetrievalResult } from './useDataLakeRetrievals';

import useDataLake from '../hooks/useDataLake';

type Props = {
  retrieval: DataLakeRetrievalResult;
};

const DataLakeRetrievalActions = ({ retrieval }: Props) => {
  const [showConfirmDelete, setShowConfirmDelete] = useState(false);
  const { onDeleteDataLakeRetrievalOperationMutation } = useDataLake(retrieval?.stream_id);
  const { pathname } = useLocation();
  const sendTelemetry = useSendTelemetry();
  
  const onConfirmDelete = () => {
    onDeleteDataLakeRetrievalOperationMutation({
      streamId: retrieval?.stream_id,
      retrievalOperationId: retrieval?.id,
    });
  };

  const handleDelete = (event: SyntheticEvent<HTMLButtonElement>) => {
    event.stopPropagation();
    setShowConfirmDelete(true);

    sendTelemetry(TELEMETRY_EVENT_TYPE.DATALAKE.RETRIEVALS.DELETE_RETRIEVAL_CLICKED, {
      app_pathname: getPathnameWithoutId(pathname),
    });
  };

  const getIndicesQueryString = (restoreIndices: Array<RestoreIndex>) =>
    restoreIndices.reduce((acc, indice, currentIndex, arr) => {
      if (currentIndex + 1 === arr.length) {
        return `${acc}_index:${indice.index_name}`;
      }

      return `${acc}_index:${indice.index_name} OR `;
    }, '');

  return (
    <>
      {showConfirmDelete && (
        <BootstrapModalConfirm
          showModal={showConfirmDelete}
          onConfirm={() => onConfirmDelete()}
          onCancel={() => setShowConfirmDelete(false)}
          title="Are you sure?">
          <>
            <p>
              You are about to delete retrieval operation done at{' '}
              <b>
                <Timestamp dateTime={retrieval?.retrieved_at} />
              </b>
              . This will delete <b>{retrieval?.restore_indices?.length}</b> indices.
            </p>

            <p>Do you want to continue?</p>
          </>
        </BootstrapModalConfirm>
      )}

      <ButtonToolbar className="pull-right">
        <LinkContainer
          to={SearchLink.builder()
            .query(createElasticsearchQueryString(getIndicesQueryString(retrieval?.restore_indices)))
            .timerange({
              type: 'absolute',
              from: retrieval?.timestamp_from,
              to: retrieval?.timestamp_to,
            })
            .build()
            .toURL()}>
          <Button
            bsSize="xsmall"
            onClick={() => {
              sendTelemetry(TELEMETRY_EVENT_TYPE.DATALAKE.RETRIEVALS.SHOW_MESSAGES_BUTTON_CLICKED, {
                app_pathname: getPathnameWithoutId(pathname),
              });
            }}>
            Show messages
          </Button>
        </LinkContainer>
        <IfPermitted permissions={['data_lake_archive:delete']}>
          <Button bsSize="xsmall" bsStyle="danger" onClick={handleDelete}>
            Delete
          </Button>
        </IfPermitted>
      </ButtonToolbar>
    </>
  );
};

export default DataLakeRetrievalActions;
